/**
 * Shared Core Utilities
 * Centralized functions used across Settings and Dashboard systems
 * Maintains existing functionality while providing reusable patterns
 */

import { kvGet, kvGetRaw, kvRemove, kvSet, listKvKeys } from '../infrastructure/storage/kvAccess';
import compressedLocalStorage, {
  shouldCompressKvKey,
  unwrapEnvelopeRawStringToInnerJsonString
} from './localStorageCompression.js';

/** Avoid spamming the console when recovery runs early in boot. */
const loggedBusinessIdRecoveries = new Set();

/**
 * Get current business ID from KV (Phase 0: localStorage-backed engine)
 * CENTRALIZED - All other implementations should use this one.
 *
 * Default allowFallback=false — never invent/pin a foreign bucket from leftover
 * payloads on a multi-business PC (wrong catalog / fake écarts after new login).
 * Opt-in allowFallback=true only returns a recovered id; it does NOT pin session.
 */
export const getCurrentBusinessId = (options = {}) => {
  const { allowFallback = false, throwOnError = false } = options;

  try {
    // 1. Try currentBusiness first (most reliable)
    const currentBusiness = kvGet('currentBusiness');
    if (currentBusiness) {
      // Value may be object (engine) or legacy JSON string
      let business;
      if (typeof currentBusiness === 'string') {
        // Check for corrupted data
        if (currentBusiness === '[object Object]' || currentBusiness === '"[object Object]"') {
          console.warn('⚠️ [SHARED CORE] Corrupted currentBusiness data, cleaning up');
          kvRemove('currentBusiness');
        } else {
          try {
            business = JSON.parse(currentBusiness);
          } catch (parseError) {
            console.error('Error parsing currentBusiness:', parseError);
            return null;
          }
        }
      } else if (typeof currentBusiness === 'object' && currentBusiness !== null) {
        // Already parsed
        business = currentBusiness;
      }
      
      if (business?.id) {
        return normalizeBusinessStorageId(business.id);
      }
    }

    // 2. Try currentUser.business
    const currentUser = kvGet('currentUser');
    if (currentUser) {
      // Value may be object (engine) or legacy JSON string
      let user;
      if (typeof currentUser === 'string') {
        // Check for corrupted data
        if (currentUser === '[object Object]' || currentUser === '"[object Object]"') {
          console.warn('⚠️ [SHARED CORE] Corrupted currentUser data, cleaning up');
          kvRemove('currentUser');
        } else {
          try {
            user = JSON.parse(currentUser);
          } catch (parseError) {
            console.error('Error parsing currentUser:', parseError);
            return null;
          }
        }
      } else if (typeof currentUser === 'object' && currentUser !== null) {
        // Already parsed
        user = currentUser;
      }
      
      if (user) {
        if (user.type === 'business' && user.business?.id) {
          return normalizeBusinessStorageId(user.business.id);
        } else if (user.business?.auth_user_id) {
          return normalizeBusinessStorageId(user.business.auth_user_id);
        } else if (user.user?.id) {
          return normalizeBusinessStorageId(user.user.id);
        } else if (user.id) {
          return normalizeBusinessStorageId(user.id);
        }
      }
    }
    
    // 3. Opt-in read-only recovery — never pin (avoids sticky wrong business on shared PC)
    if (allowFallback) {
      const recoveredBusinessId = inferBusinessIdFromExistingPayloads();
      if (recoveredBusinessId) {
        if (
          process.env.NODE_ENV === 'development' &&
          !loggedBusinessIdRecoveries.has(recoveredBusinessId)
        ) {
          loggedBusinessIdRecoveries.add(recoveredBusinessId);
          console.info(
            '[businessId] Recovered id from storage payloads (not pinned):',
            recoveredBusinessId
          );
        }
        return recoveredBusinessId;
      }
    }

    // 4. Error handling
    if (throwOnError) {
      throw new Error('Business ID not found and no fallback available');
    }

    if (kvGet('currentUser') || kvGet('currentBusiness')) {
      console.warn('getCurrentBusinessId: No Business ID found');
    }
    return null;
  } catch (error) {
    console.error('Error getting business ID:', error);
    
    if (throwOnError) {
      throw error;
    }
    
    return allowFallback ? 'offline_mode' : null;
  }
};

/**
 * Some profiles store business.id with a redundant "business_" prefix.
 * Persisted bucket must be exactly business_<slug>_data (single prefix).
 */
export const normalizeBusinessStorageId = (id) => {
  if (id == null || id === '') return id;
  const s = String(id);
  return s.startsWith('business_') ? s.slice('business_'.length) : s;
};

function businessIdFromMergedStorageKey(key) {
  if (key == null || typeof key !== 'string') return null;
  const m = key.match(/^business_(.+)_data$/);
  return m ? m[1] : null;
}

function collectionSize(value) {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === 'object') return Object.keys(value).length;
  return 0;
}

/**
 * Top-Level-Felder die in entity_* gespiegelt sind (Phase 20).
 * Note: inventoryLedgerMeta / stockMovementsArchive / stockSnapshots have no entity_*
 * tables — they must stay in the KV/thin shell (see NO_KV_BLOB_SHELL_FIELDS). Listing
 * them here caused strip + thin-shell persist to drop rebuilt snapshots after reproject.
 */
export const ENTITY_PAYLOAD_FIELD_NAMES = [
  'sales',
  'purchases',
  'lots',
  'products',
  'customers',
  'suppliers',
  'stockData',
  'invoices',
  'orders',
  'expenses',
  'invoicePayments',
  'supplierDebts',
  'customerDebts',
  'customerInvoices',
  'supermarketPayments',
  'supermarketInvoices',
  'expiredProducts',
  'notifications',
  'workerSessions',
  'reports',
  'discrepancyManagement',
  'stockLocations',
  'persistentStockData',
  'offlineQueue',
  'businessInfo',
  'userInfo',
  'metadata',
  'stockMovements'
];

function entityPayloadFieldHasContent(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

/** KV-Snapshot enthält nur Meta — Daten leben in entity_*. */
export function isEntityShellBlob(parsed) {
  if (!parsed || typeof parsed !== 'object') return false;
  if (parsed.entityStoreNoKvBlob === true) return true;
  if (parsed.entityStorePrimary === true) return true;
  if (!Number.isFinite(Number(parsed.payloadVersion))) return false;
  return !ENTITY_PAYLOAD_FIELD_NAMES.some((field) =>
    entityPayloadFieldHasContent(parsed[field])
  );
}

/**
 * Non-entity fields held only in the thin KV shell under NO_KV_BLOB
 * (entity_* has no table — openTickets, recipes, …).
 * Kept in sharedCore so readBest can re-apply them when fat legacy blobs score higher.
 */
export const NO_KV_BLOB_SHELL_FIELD_NAMES = [
  'restaurantDishes',
  'stockConsumptions',
  'restaurantTables',
  'openTickets',
  'drafts',
  'recipes',
  'productionOrders'
];

/**
 * Fat legacy blobs often win score (products/lots) and hide thin-shell fields written
 * later on the canonical key. Overlay shell fields from entity-shell candidates
 * (newer payloadVersion wins).
 *
 * @param {object|null|undefined} parsed
 * @param {Array<{ parsed?: object, recency?: { version: number, updatedAtMs: number } }>} candidates
 * @returns {object|null|undefined}
 */
export function overlayNoKvBlobShellFieldsFromCandidates(parsed, candidates) {
  if (!parsed || typeof parsed !== 'object') return parsed;
  if (!Array.isArray(candidates) || candidates.length === 0) return parsed;

  const shells = candidates
    .filter((c) => c?.parsed && isEntityShellBlob(c.parsed))
    .slice()
    .sort((a, b) => {
      const av = a.recency?.version ?? payloadRecencyMeta(a.parsed).version;
      const bv = b.recency?.version ?? payloadRecencyMeta(b.parsed).version;
      if (av !== bv) return av - bv;
      const at = a.recency?.updatedAtMs ?? payloadRecencyMeta(a.parsed).updatedAtMs;
      const bt = b.recency?.updatedAtMs ?? payloadRecencyMeta(b.parsed).updatedAtMs;
      return at - bt;
    });

  if (shells.length === 0) return parsed;

  const next = { ...parsed };
  for (const c of shells) {
    for (const field of NO_KV_BLOB_SHELL_FIELD_NAMES) {
      if (c.parsed[field] !== undefined) {
        next[field] = c.parsed[field];
      }
    }
  }
  return next;
}

/** Normalisiert lots[] und klont für sicheres Mergen. */
export function normalizeBusinessPayloadShape(payload) {
  if (!payload || typeof payload !== 'object') return {};
  try {
    const out = JSON.parse(JSON.stringify(payload));
    if (out.lots && !Array.isArray(out.lots) && typeof out.lots === 'object') {
      out.lots = Object.values(out.lots);
    }
    return out;
  } catch {
    const out = { ...payload };
    if (out.lots && !Array.isArray(out.lots) && typeof out.lots === 'object') {
      out.lots = Object.values(out.lots);
    }
    return out;
  }
}

/**
 * Merged patch in base ohne reichere Felder zu löschen (stale stockData-Write etc.).
 */
export function mergeBusinessPayloadPreserveRicher(base, patch) {
  const merged = normalizeBusinessPayloadShape(base);
  const p = normalizeBusinessPayloadShape(patch);
  const arrayFields = [
    'products',
    'lots',
    'purchases',
    'suppliers',
    'customers',
    'sales',
    'expenses',
    'orders',
    'invoices',
    'invoicePayments',
    'stockMovements',
    'stockMovementsArchive',
    'stockSnapshots',
    'restaurantDishes',
    'stockConsumptions',
    'restaurantTables',
    'openTickets',
    'drafts',
    'recipes'
  ];

  for (const field of arrayFields) {
    if (p[field] === undefined) continue;
    const baseN = collectionSize(merged[field]);
    const patchN = collectionSize(p[field]);
    if (patchN >= baseN || baseN === 0) {
      merged[field] = p[field];
    }
  }

  if (p.stockData !== undefined) {
    merged.stockData = {
      ...(merged.stockData && typeof merged.stockData === 'object' ? merged.stockData : {}),
      ...(p.stockData && typeof p.stockData === 'object' ? p.stockData : {})
    };
  }

  for (const field of Object.keys(p)) {
    if (arrayFields.includes(field) || field === 'stockData') continue;
    if (p[field] !== undefined) {
      merged[field] = p[field];
    }
  }

  return merged;
}

/** true wenn key ein merged business_*_data Blob ist (kein direktes autoSave). */
export function isBusinessMergedPayloadKey(key) {
  return businessIdFromMergedStorageKey(key) != null;
}

/** Canonical merged payload key (KV / effective localStorage id) */
export const businessDataStorageKey = (businessId) => {
  if (businessId == null || businessId === '') return null;
  const n = normalizeBusinessStorageId(businessId);
  return `business_${n}_data`;
};

/**
 * Low-Level-Write für business_*_data (Kompression/KV).
 * App-Code: queueBusinessPayloadWrite / Repositories bevorzugen.
 */
export const writeBusinessMergedPayload = (key, payload, options = {}) => {
  if (key == null || key === '') {
    return { success: false, error: 'missing key' };
  }

  let toWrite = normalizeBusinessPayloadShape(payload);
  const businessId = businessIdFromMergedStorageKey(key);
  const authoritativeFields = options.authoritativeFields || [];
  const authoritativeSnap = {};
  for (const field of authoritativeFields) {
    if (toWrite[field] !== undefined) {
      authoritativeSnap[field] = toWrite[field];
    }
  }
  if (businessId && !toWrite.entityStorePrimary) {
    const best = readBestBusinessMergedPayload(businessId);
    if (best.parsed && !isEntityShellBlob(best.parsed)) {
      toWrite = mergeBusinessPayloadPreserveRicher(best.parsed, toWrite);
    }
  }
  for (const field of authoritativeFields) {
    if (authoritativeSnap[field] !== undefined) {
      toWrite[field] = authoritativeSnap[field];
    }
  }

  if (shouldCompressKvKey(key)) {
    const result = compressedLocalStorage.setItem(key, toWrite);
    if (businessId) invalidateMergedPayloadSessionCache(businessId);
    return result;
  }
  kvSet(key, toWrite);
  if (businessId) invalidateMergedPayloadSessionCache(businessId);
  return { success: true, compressed: false };
};

/**
 * Alle möglichen KV-Keys für merged business payload (Phase 1 / Legacy-IDs).
 * Verhindert „Lesen null → Schreiben nur purchases“ → alle anderen Felder weg.
 */
export function listBusinessMergedPayloadKeyCandidates(businessId) {
  if (businessId == null || businessId === '') return [];
  const union = new Set();

  const addKeysForOne = (id) => {
    if (id == null || id === '') return;
    const s = String(id);
    const n = normalizeBusinessStorageId(id);
    const add = (k) => {
      if (k) union.add(k);
    };
    add(businessDataStorageKey(id));
    add(`business_${s}_data`);
    if (n !== s) add(`business_${n}_data`);
    add(`business_business_${n}_data`);
  };

  addKeysForOne(businessId);
  const norm = normalizeBusinessStorageId(businessId);
  if (norm && String(norm) !== String(businessId)) {
    addKeysForOne(norm);
  }

  return Array.from(union);
}

function parseBusinessPayloadJson(raw) {
  if (raw == null || raw === '') return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
}

/** Felder, die forceMigration in Elkheir:v1:biz:{id}:{collection} auslagert. */
const ELKHEIR_V1_MERGED_COLLECTIONS = [
  'products',
  'suppliers',
  'purchases',
  'sales',
  'lots',
  'categories',
  'settings',
  'expiredProducts',
  'notifications',
  'stockMovements'
];

/**
 * Rekonstruiert merged business_*_data aus Elkheir:v1-Shard-Keys (nach removeOldData / Cleanup).
 */
export function reconstructBusinessMergedPayloadFromElkheirV1(businessId) {
  if (businessId == null || businessId === '') return null;

  const metaKey = `Elkheir:v1:biz:${businessId}:meta`;
  if (kvGet(metaKey) == null && kvGetRaw(metaKey) == null) return null;

  const payload = {};
  let hasContent = false;

  for (const collection of ELKHEIR_V1_MERGED_COLLECTIONS) {
    const key = `Elkheir:v1:biz:${businessId}:${collection}`;
    const value = kvGet(key);
    if (value === undefined || value === null) continue;
    payload[collection] = value;
    if (Array.isArray(value) && value.length > 0) hasContent = true;
    else if (value && typeof value === 'object' && Object.keys(value).length > 0) {
      hasContent = true;
    }
  }

  if (!hasContent) return null;
  return normalizeBusinessPayloadShape(payload);
}

/** payloadVersion + payloadUpdatedAt für Tie-Break (neuerer Kanon gewinnt). */
export function payloadRecencyMeta(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return { version: 0, updatedAtMs: 0 };
  }
  const version = Number(parsed.payloadVersion);
  const updatedAtMs = Date.parse(parsed.payloadUpdatedAt || '') || 0;
  return {
    version: Number.isFinite(version) && version >= 0 ? version : 0,
    updatedAtMs
  };
}

function shouldPreferPayloadCandidate(next, current, nextKey, currentKey, canonicalKey) {
  if (next.score > current.score) return true;
  if (next.score < current.score) return false;
  if (next.recency.version !== current.recency.version) {
    return next.recency.version > current.recency.version;
  }
  if (next.recency.updatedAtMs !== current.recency.updatedAtMs) {
    return next.recency.updatedAtMs > current.recency.updatedAtMs;
  }
  if (nextKey === canonicalKey && currentKey !== canonicalKey) return true;
  return false;
}

/** Größerer Score = vollständigerer Blob (nicht nur purchases nach Fehl-Schreiben). */
export function scoreBusinessMergedPayload(parsed) {
  if (!parsed || typeof parsed !== 'object') return 0;
  let score = 0;
  const bump = (v, weight = 1) => {
    if (Array.isArray(v)) score += v.length * weight;
    else if (v && typeof v === 'object') score += Object.keys(v).length * weight;
  };
  bump(parsed.products, 3);
  bump(parsed.lots, 3);
  bump(parsed.purchases, 2);
  bump(parsed.sales, 2);
  bump(parsed.suppliers, 1);
  bump(parsed.customers, 1);
  bump(parsed.stockData, 2);
  bump(parsed.stockMovements, 1);
  return score;
}

/**
 * Liest nur den kanonischen Key business_{id}_data (kein Legacy-Scan).
 */
export function readCanonicalBusinessMergedPayload(businessId) {
  const canonicalKey = businessDataStorageKey(businessId);
  if (!canonicalKey) {
    return { raw: null, parsed: null, key: null, score: 0, recency: payloadRecencyMeta(null) };
  }
  const rawStored = kvGetRaw(canonicalKey);
  if (rawStored == null || rawStored === '') {
    return { raw: null, parsed: null, key: canonicalKey, score: 0, recency: payloadRecencyMeta(null) };
  }
  const unwrapped = unwrapEnvelopeRawStringToInnerJsonString(canonicalKey, rawStored);
  if (unwrapped == null || unwrapped === '') {
    return { raw: null, parsed: null, key: canonicalKey, score: 0, recency: payloadRecencyMeta(null) };
  }
  const parsed = parseBusinessPayloadJson(unwrapped);
  if (!parsed) {
    return { raw: null, parsed: null, key: canonicalKey, score: 0, recency: payloadRecencyMeta(null) };
  }
  return {
    raw: unwrapped,
    parsed,
    key: canonicalKey,
    score: scoreBusinessMergedPayload(parsed),
    recency: payloadRecencyMeta(parsed)
  };
}

/**
 * Liest Business-Blob: zuerst kanonischer Key, sonst Legacy mit Score + payloadVersion.
 * Session-Cache vermeidet wiederholte KV-Scans während eines App-Laufs.
 */
const _mergedPayloadSessionCache = new Map();

export function invalidateMergedPayloadSessionCache(businessId) {
  if (businessId != null && businessId !== '') {
    _mergedPayloadSessionCache.delete(String(businessId));
  } else {
    _mergedPayloadSessionCache.clear();
  }
}

export function readBestBusinessMergedPayload(businessId) {
  if (businessId == null || businessId === '') {
    return { raw: null, parsed: null, key: null, score: 0 };
  }

  const cacheKey = String(businessId);
  const sessionHit = _mergedPayloadSessionCache.get(cacheKey);
  if (sessionHit) {
    return sessionHit;
  }

  const canonicalKey = businessDataStorageKey(businessId);
  const candidates = [];

  const consider = (key, parsed, raw) => {
    if (!parsed) return;
    const score = scoreBusinessMergedPayload(parsed);
    candidates.push({
      key,
      parsed,
      raw,
      score,
      recency: payloadRecencyMeta(parsed)
    });
  };

  const canonical = readCanonicalBusinessMergedPayload(businessId);
  if (
    canonical.parsed &&
    (canonical.score > 0 || isEntityShellBlob(canonical.parsed))
  ) {
    consider(canonical.key, canonical.parsed, canonical.raw);
  }

  for (const key of listBusinessMergedPayloadKeyCandidates(businessId)) {
    if (key === canonicalKey) continue;
    const rawStored = kvGetRaw(key);
    if (rawStored == null || rawStored === '') continue;
    const unwrapped = unwrapEnvelopeRawStringToInnerJsonString(key, rawStored);
    if (unwrapped == null || unwrapped === '') continue;
    consider(key, parseBusinessPayloadJson(unwrapped), unwrapped);
  }

  const elkParsed = reconstructBusinessMergedPayloadFromElkheirV1(businessId);
  if (elkParsed) {
    try {
      consider('Elkheir:v1:reconstructed', elkParsed, JSON.stringify(elkParsed));
    } catch {
      consider('Elkheir:v1:reconstructed', elkParsed, null);
    }
  }

  if (candidates.length === 0) {
    return { raw: null, parsed: null, key: null, score: 0 };
  }

  let best = candidates[0];
  for (let i = 1; i < candidates.length; i += 1) {
    const next = candidates[i];
    if (shouldPreferPayloadCandidate(next, best, next.key, best.key, canonicalKey)) {
      best = next;
    }
  }

  // Recovery: mehrere Legacy-Blobs nur wenn kein belastbarer Kanon
  const canonicalCandidate = candidates.find((c) => c.key === canonicalKey);
  const canonicalUsable = canonicalCandidate && canonicalCandidate.score > 0;
  if (!canonicalUsable && candidates.length > 1) {
    const mergedFromCandidates = candidates.reduce(
      (acc, item) => mergeBusinessPayloadPreserveRicher(acc, item.parsed),
      {}
    );
    const mergedScore = scoreBusinessMergedPayload(mergedFromCandidates);
    const mergedRecency = payloadRecencyMeta(mergedFromCandidates);
    const merged = {
      key: 'merged_candidates',
      parsed: mergedFromCandidates,
      raw: null,
      score: mergedScore,
      recency: mergedRecency
    };
    try {
      merged.raw = JSON.stringify(mergedFromCandidates);
    } catch {
      merged.raw = null;
    }
    if (shouldPreferPayloadCandidate(merged, best, merged.key, best.key, canonicalKey)) {
      best = merged;
      if (process.env.NODE_ENV === 'development') {
        console.info('[sharedCore] readBest merged legacy candidates (no canonical)', {
          businessId: String(businessId),
          candidateCount: candidates.length,
          mergedScore
        });
      }
    }
  }

  // Re-apply thin-shell fields (recipes, openTickets, …) from entity-shell candidates.
  // Without this, a fat legacy blob with higher score hides shell data written later.
  const parsedWithShell = overlayNoKvBlobShellFieldsFromCandidates(best.parsed, candidates);

  const result = {
    raw: best.raw,
    parsed: parsedWithShell,
    key: best.key,
    score: best.score,
    payloadVersion: best.recency.version,
    payloadUpdatedAt: best.recency.updatedAtMs
      ? new Date(best.recency.updatedAtMs).toISOString()
      : undefined
  };
  _mergedPayloadSessionCache.set(cacheKey, result);
  return result;
}

/** Raw JSON string — vollständigster Kandidat unter allen Legacy-Keys. */
export const readBusinessMergedPayloadRaw = (businessId) => {
  return readBestBusinessMergedPayload(businessId).raw;
};

/** Parsed object — bevorzugt für Lesepfade (kein JSON.parse nötig). */
export const readBusinessMergedPayloadParsed = (businessId) => {
  const best = readBestBusinessMergedPayload(businessId);
  return best.parsed ?? null;
};

/**
 * Schreibt den besten gefundenen Blob auf den kanonischen Key (Recovery nach Teil-Overwrite).
 */
export function consolidateBusinessMergedPayloadToCanonical(businessId) {
  const canonicalKey = businessDataStorageKey(businessId);
  if (!canonicalKey) return { migrated: false, reason: 'no_key' };

  const best = readBestBusinessMergedPayload(businessId);
  if (!best.parsed || best.score <= 0) {
    return { migrated: false, reason: 'no_data', score: best.score };
  }

  const canonicalRaw = kvGetRaw(canonicalKey);
  const canonicalParsed = canonicalRaw
    ? parseBusinessPayloadJson(
        unwrapEnvelopeRawStringToInnerJsonString(canonicalKey, canonicalRaw)
      )
    : null;
  const canonicalScore = scoreBusinessMergedPayload(canonicalParsed);

  if (best.key === canonicalKey && canonicalScore >= best.score) {
    return { migrated: false, reason: 'already_canonical', score: canonicalScore };
  }

  if (best.score > canonicalScore) {
    const result = writeBusinessMergedPayload(canonicalKey, best.parsed);
    if (result?.success === false) {
      return { migrated: false, reason: result.error || 'write_failed', score: best.score };
    }
    if (process.env.NODE_ENV === 'development') {
      console.info('[sharedCore] Business payload recovered to canonical key', {
        businessId: String(businessId),
        fromKey: best.key,
        canonicalKey,
        previousScore: canonicalScore,
        recoveredScore: best.score
      });
    }
    return {
      migrated: true,
      fromKey: best.key,
      canonicalKey,
      previousScore: canonicalScore,
      score: best.score
    };
  }

  return { migrated: false, reason: 'canonical_sufficient', score: canonicalScore };
};

/**
 * Generate fallback Business ID for offline mode
 */
const generateFallbackBusinessId = () => {
  try {
    // Hardware-based fallback
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Fallback Business ID', 2, 2);
    
    const fingerprint = canvas.toDataURL();
    const hash = simpleHash(fingerprint);
    
    return `offline_${hash.substring(0, 8)}`;
  } catch (error) {
    console.warn('Fallback Business ID generation failed:', error);
    return 'offline_mode';
  }
};

/**
 * Simple hash function for fallback IDs
 */
const simpleHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16);
};

export function inferBusinessIdFromExistingPayloads() {
  try {
    const ids = new Set();
    const registeredIds = new Set();

    const offlineRaw = kvGet('offline_businesses');
    const offlineList = Array.isArray(offlineRaw) ? offlineRaw : [];
    for (const row of offlineList) {
      const id = normalizeBusinessStorageId(row?.id);
      if (id) {
        ids.add(id);
        registeredIds.add(id);
      }
    }

    for (const key of listKvKeys()) {
      const m = String(key || '').match(/^business_(.+)_data$/);
      if (m?.[1]) ids.add(normalizeBusinessStorageId(m[1]));
      const meta = String(key || '').match(/^Elkheir:v1:meta:business_payload_v1:(.+)$/);
      if (meta?.[1]) ids.add(normalizeBusinessStorageId(meta[1]));
    }

    let bestId = null;
    let bestScore = -1;
    let shellId = null;
    let bestRegisteredId = null;
    let bestRegisteredScore = -1;

    for (const id of ids) {
      if (!id || isEphemeralOfflineBusinessId(id, registeredIds)) continue;
      const { parsed, score } = readBestBusinessMergedPayload(id);
      if (parsed && isEntityShellBlob(parsed)) {
        if (registeredIds.has(id)) shellId = id;
        continue;
      }
      if (registeredIds.has(id) && score > bestRegisteredScore) {
        bestRegisteredScore = score;
        bestRegisteredId = id;
      }
      if (score > bestScore) {
        bestScore = score;
        bestId = id;
      }
    }

    if (bestRegisteredId) return bestRegisteredId;
    if (bestScore > 0) return bestId;
    return shellId;
  } catch (error) {
    console.warn('inferBusinessIdFromExistingPayloads failed:', error);
    return null;
  }
}

/** Canvas-Fallback-IDs (offline_xxxxxxxx) — nur gültig wenn in offline_businesses registriert. */
export function isEphemeralOfflineBusinessId(businessId, registeredIds = null) {
  const id = normalizeBusinessStorageId(businessId);
  if (!id || !/^offline_[a-f0-9]{6,10}$/i.test(id)) return false;
  if (registeredIds instanceof Set) return !registeredIds.has(id);
  try {
    const offlineRaw = kvGet('offline_businesses');
    const list = Array.isArray(offlineRaw) ? offlineRaw : [];
    return !list.some((b) => normalizeBusinessStorageId(b?.id) === id);
  } catch {
    return true;
  }
}

/**
 * Create error handler with notification system
 * Centralized error handling pattern
 */
export const createErrorHandler = (notificationSystem = null) => (error) => {
  console.error('Application Error:', error);
  
  const errorMessage = error.message || 'An unexpected error occurred';
  
  if (notificationSystem) {
    notificationSystem.error(errorMessage);
  }
  
  return {
    success: false,
    error: errorMessage,
    data: null
  };
};

/**
 * Function wrapper with error handling
 * Wraps async functions with consistent error handling
 */
export const withErrorHandling = (fn, errorHandler) => async (...args) => {
  try {
    const result = await fn(...args);
    return { success: true, data: result, error: null };
  } catch (error) {
    return errorHandler(error);
  }
};

/**
 * Validate input against rules
 * Centralized validation system
 */
export const validateField = (value, rules) => {
  for (const rule of rules) {
    const error = rule(value);
    if (error) return { valid: false, error };
  }
  return { valid: true, error: null };
};

/**
 * Create validation rule
 * Factory function for validation rules
 */
export const createValidationRule = (validator, errorMessage) => (value) => {
  return validator(value) ? null : errorMessage;
};

/**
 * Common validation rules
 * Reusable validation patterns
 */
export const validationRules = {
  required: (fieldName) => createValidationRule(
    (value) => value && value.toString().trim().length > 0,
    `${fieldName} is required`
  ),
  minLength: (min) => createValidationRule(
    (value) => !value || value.length >= min,
    `Minimum length is ${min} characters`
  ),
  maxLength: (max) => createValidationRule(
    (value) => !value || value.length <= max,
    `Maximum length is ${max} characters`
  ),
  pattern: (regex) => createValidationRule(
    (value) => !value || regex.test(value),
    'Invalid format'
  ),
  number: (min = 0, max = Infinity) => createValidationRule(
    (value) => {
      const num = Number(value);
      return !isNaN(num) && num >= min && num <= max;
    },
    `Must be a number between ${min} and ${max}`
  ),
  email: () => createValidationRule(
    (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    'Invalid email format'
  ),
  phone: () => createValidationRule(
    (value) => !value || /^[+]?[0-9\s\-()]{8,}$/.test(value),
    'Invalid phone number format'
  )
};

/**
 * Debounce function
 * Prevents excessive function calls
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function
 * Limits function execution frequency
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Deep clone object
 * Safe object cloning
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};

/**
 * Safe JSON parse
 * Prevents JSON parsing errors
 */
export const safeJsonParse = (jsonString, defaultValue = null) => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('JSON parse error:', error);
    return defaultValue;
  }
};

/**
 * Safe JSON stringify
 * Prevents JSON stringify errors
 */
export const safeJsonStringify = (obj, defaultValue = '{}') => {
  try {
    return JSON.stringify(obj);
  } catch (error) {
    console.warn('JSON stringify error:', error);
    return defaultValue;
  }
};

/**
 * Generate unique ID
 * Creates unique identifiers
 */
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Format file size
 * Human readable file sizes
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if value is empty
 * Comprehensive empty value check
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Capitalize first letter
 * String utility
 */
export const capitalize = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Convert to title case
 * String utility
 */
export const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

